import React, { useEffect, useState } from 'react';
import { Button, Dropdown, DropdownButton, Modal, Table } from 'react-bootstrap';
import { Campaign } from '../../../model/campaign/Campaign';
import { CampaignsService } from '../CampaignsService';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { useNavigate } from 'react-router-dom';
import { formatDate, getCorrectEnums, hasAccessRight } from '../../../utils/Functions';
import { AdminAclEnum } from '../../../enum/AdminAclEnum';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { BsTrash } from 'react-icons/bs';
import { IUser } from '../../../model/user/IUser';
import { calculateFee } from '../../../utils/helpers/FeeCaluculation';
import { useDispatch } from 'react-redux';
import generalSlice from '../../../store/general/GeneralSlice';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import { FaTimes } from 'react-icons/fa';
import { CustomImageHandler } from '../../../shared/components/img/CustomImageHandler';
import { CampaignFilter } from './CampaignFilter';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';

interface IShowModal {
    show: boolean;
    id: number | undefined;
    title: string;
    showSupervisor: boolean;
    showClient: boolean;
    assignedSupervisors: IUser[];
    assignedClients: IUser[];
}

interface IManagersShowModal {
    show: boolean;
    managers: IUser[];
}

const CAMPAIGN_COLUMNS: ITableColumn[] = [
    { label: 'ID' },
    { label: 'Campaign name' },
    { label: 'Company' },
    { label: 'Client' },
    { label: 'Phase' },
    { label: 'Fee' },
    { label: 'Package' },
    { label: 'Publication period' },
    { label: 'Status' },
    { label: 'Type' },
    { label: 'Supervisor' },
    { label: 'Total spent' },
    { label: 'Actions' },
];

export function Campaigns() {
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [state, setState] = useState({
        page: 1,
        perPage: 15,
        totalPages: 0,
    });
    const [filterParams, setFilterParams] = useState({
        id: undefined,
        title: undefined,
        isCompleted: undefined,
        supervisorId: undefined,
        companyId: undefined,
        clientId: undefined,
    });

    const [campaign, setCampaign] = useState<Campaign[]>([]);
    const [supervisors, setSupervisors] = useState<IUser[]>([]);
    const [supervisorsBackup, setSupervisorsBackup] = useState<IUser[]>([]);
    const [change, setChange] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<IShowModal>({
        show: false,
        id: undefined,
        title: '',
        showSupervisor: false,
        showClient: false,
        assignedSupervisors: [],
        assignedClients: [],
    });
    const [showManagerModal, setShowManagerModal] = useState<IManagersShowModal>({
        show: false,
        managers: [],
    });
    const [supervisorId, setSupervisorId] = useState<number>(0);
    const [triggerSupervisor, setTriggerSupervisor] = useState<boolean>(false);
    const [filterSupervisor, setFilterSupervisor] = useState('');
    const [selectedUser, setSetSelectedUser] = useState<Partial<IUser>>({});
    const closeSupervisorModal = () => {
        setShowModal(prev => ({ ...prev, showSupervisor: false }));
        setFilterSupervisor('');
        setSetSelectedUser({});
    };
    useEffect(() => {
        setDataLoading(true);
        CampaignsService.getCampaigns({
            ...state,
            ...filterParams,
            isCompleted: filterParams?.isCompleted === 'undefined' ? undefined : filterParams?.isCompleted,
        })
            .then(data => {
                setCampaign(data.data);
                setState(prev => ({ ...prev, totalPages: Math.ceil(data.count / state.perPage) }));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }, [change]);

    useEffect(() => {
        CampaignsService.getSupervisor({ perPage: 1000000 })
            .then(res => {
                setSupervisors(res.data);
                const result = res.data?.map((item: IUser) => {
                    return { ...item, displayName: `${item.firstName} ${item.lastName}` };
                });
                setSupervisorsBackup(result);
            })
            .catch(err => {
                ErrorToast(err);
            });
    }, [triggerSupervisor]);

    useEffect(() => {
        const result = supervisorsBackup.filter(user =>
            user.displayName?.toLowerCase().includes(filterSupervisor?.toLowerCase())
        );

        let arr = result;
        const length =
            result.length < showModal.assignedSupervisors.length ? showModal.assignedSupervisors.length : result.length;
        for (let i = 0; i < length; i++) {
            if (
                showModal.assignedSupervisors[i] &&
                arr.filter(item => item.id !== showModal.assignedSupervisors[i].id)
            ) {
                arr = arr.filter(item => item.id !== showModal.assignedSupervisors[i].id);
            }
        }
        setSupervisors(arr);
    }, [filterSupervisor]);

    function handleClick() {
        setChange(!change);
        setState({
            ...state,
            page: 1,
        });
    }

    function handleChange(evt: any) {
        const value = evt.target.value.trim();
        setFilterParams({
            ...filterParams,
            [evt.target.name]: value,
        });
    }

    const handlePages = (updatePage: number) => {
        setState({ ...state, page: updatePage });
        setChange(!change);
    };

    const completeCampaign = () => {
        CampaignsService.completeCampaign(showModal.id as number)
            .then(() => {
                setShowModal({ ...showModal, show: false });
                SuccessToast('Successfully completed');
                setChange(!change);
            })
            .catch(err => ErrorToast(err));
    };

    useEffect(() => {
        if (!!supervisorId) {
            CampaignsService.assignSupervisor(supervisorId, showModal.id as number)
                .then(() => {
                    SuccessToast('Successfully action');
                    setFilterSupervisor('');
                    setChange(!change);
                    setSupervisorId(0);
                    const supervisor = supervisorsBackup.find(item => item.id === supervisorId);
                    dispatch(
                        generalSlice.actions.setConfirmModal({
                            show: false,
                        })
                    );
                    setShowModal({
                        ...showModal,
                        assignedSupervisors: [...(showModal?.assignedSupervisors as any), supervisor],
                    });
                })
                .catch(err => ErrorToast(err));
        }
    }, [supervisorId]);

    useEffect(() => {
        if (showModal.showSupervisor) {
            let arr = supervisors;
            for (let i = 0; i < supervisors.length; i++) {
                if (
                    showModal.assignedSupervisors[i] &&
                    arr.filter(item => item.id !== showModal.assignedSupervisors[i].id)
                ) {
                    arr = arr.filter(item => item.id !== showModal.assignedSupervisors[i].id);
                }
            }
            setSupervisors(arr);
        } else {
            setTriggerSupervisor(!triggerSupervisor);
        }
    }, [showModal.showSupervisor, showModal.assignedSupervisors]);

    const removeSupervisor = (idUser: number, user: any) => {
        CampaignsService.deleteSupervisor(idUser, showModal.id as number)
            .then(() => {
                const updatedSupervisors = showModal?.assignedSupervisors?.filter(item => item.id !== idUser);
                setShowModal({ ...showModal, assignedSupervisors: updatedSupervisors });
                SuccessToast('Successfully action');
                setChange(!change);
                setSupervisors([...supervisors, user]);
                dispatch(
                    generalSlice.actions.setConfirmModal({
                        show: false,
                    })
                );
            })
            .catch(err => ErrorToast(err));
    };

    const rollBackExpired = (campaignId: number) => {
        CampaignsService.rollbackExpiredOffers(campaignId)
            .then(() => {
                setChange(!change);
                SuccessToast('Expired offers changed to active status.');
                dispatch(
                    generalSlice.actions.setConfirmModal({
                        show: false,
                    })
                );
            })
            .catch(err => ErrorToast(err));
    };

    const removeClient = (idUser: number) => {
        CampaignsService.deleteClient(idUser, showModal.id as number)
            .then(() => {
                const updatedClients = showModal?.assignedClients?.filter(item => item.id !== idUser);
                setShowModal({ ...showModal, assignedClients: updatedClients });
                SuccessToast('Successfully action');
                setChange(!change);
            })
            .catch(err => ErrorToast(err));
    };

    useEffect(() => {
        if (selectedUser?.id) {
            dispatch(
                generalSlice.actions.setConfirmModal({
                    show: true,
                    title: 'Campaign communication',
                    contentBody: (
                        <div>
                            Are you sure you want to add this{' '}
                            <span className="text-primary">
                                {selectedUser?.firstName} {selectedUser?.lastName}
                            </span>{' '}
                            as a supervisor?
                        </div>
                    ),
                    action: () => setSupervisorId(selectedUser.id as number),
                })
            );
        }
    }, [selectedUser]);

    return (
        <>
            <Modal
                show={showModal.show}
                onHide={() => setShowModal({ ...showModal, show: false })}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">Campaign completion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>Do you want to complete all offers for this {showModal?.title} campaign?</div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={() => setShowModal({ ...showModal, show: false })}>
                        No
                    </Button>
                    <Button onClick={() => completeCampaign()}>Yes</Button>
                </Modal.Footer>
            </Modal>

            <Modal
                animation={false}
                show={showModal.showSupervisor}
                onHide={closeSupervisorModal}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">Edit Supervisor</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>Choose supervisor for {showModal?.title} campaign?</div>
                    <div className="form-group col-md-6 col-sm-12 p-0 mt-2">
                        <div className="autocomplete-input zindex-5 position-relative">
                            <AutocompleteInput
                                value={filterSupervisor}
                                items={supervisors}
                                renderItems={['firstName', 'lastName']}
                                setFilter={setFilterSupervisor}
                                onSelect={setSetSelectedUser}
                            />
                        </div>
                    </div>
                    <div>
                        {showModal?.assignedSupervisors?.map((item: IUser) => {
                            return (
                                <div key={item?.email} className="d-flex shadow p-2 rounded mb-4 mt-4">
                                    <div className="col">
                                        <div className="fw-bolder">
                                            {item?.firstName} {item?.lastName}
                                        </div>
                                        <div>{item?.email}</div>
                                    </div>
                                    <div className="col-auto">
                                        <Button
                                            className="btn-danger"
                                            onClick={() => {
                                                dispatch(
                                                    generalSlice.actions.setConfirmModal({
                                                        show: true,
                                                        title: 'Campaign communication',
                                                        contentBody: (
                                                            <div>
                                                                Are you sure you want to remove this{' '}
                                                                <span className="text-primary">
                                                                    {item.firstName} {item.lastName}
                                                                </span>{' '}
                                                                as a supervisor?
                                                            </div>
                                                        ),
                                                        action: () => removeSupervisor(item.id, item),
                                                    })
                                                );
                                            }}
                                        >
                                            <BsTrash />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={closeSupervisorModal}>Close</Button>
                </Modal.Footer>
            </Modal>

            <Modal
                animation={false}
                show={showModal.showClient}
                onHide={() => setShowModal({ ...showModal, showClient: false })}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        Remove Clients from {showModal?.title} campaign
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        {showModal?.assignedClients?.map((item: IUser) => {
                            return (
                                <div key={item?.email} className="d-flex shadow p-2 rounded mb-4">
                                    <div className="col">
                                        <div className="fw-bolder">
                                            {item?.firstName} {item?.lastName}
                                        </div>
                                        <div>{item?.email}</div>
                                    </div>
                                    <div className="col-auto">
                                        <Button variant="danger" onClick={() => removeClient(item.id)}>
                                            <BsTrash />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={closeSupervisorModal}>Close</Button>
                </Modal.Footer>
            </Modal>

            <Modal
                animation={false}
                show={showManagerModal.show}
                onHide={() => setShowManagerModal({ ...showManagerModal, show: false })}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">Managers</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="card mb-5 p-4">
                        <Table hover responsive>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Photo</th>
                                    <th>Display name</th>
                                    <th className="text-right">Email</th>
                                </tr>
                            </thead>
                            <tbody>
                                {showManagerModal?.managers?.map((e: IUser) => (
                                    <tr key={e.id}>
                                        <td>{e.id}</td>
                                        <td>
                                            <CustomImageHandler
                                                altTag="user-photo"
                                                classes="rounded-circle"
                                                thumbnailPath={e?.profilePhoto?.thumbnail ?? ''}
                                                photoPath={e?.profilePhoto?.thumbnail ?? ''}
                                                style={{ width: 50, height: 50 }}
                                            />
                                        </td>
                                        <td>
                                            {e.displayName}
                                            {!!e.displayName && <br />}
                                            {e.firstName} {e.lastName}
                                        </td>
                                        <td>
                                            <ImpersonateEye id={e.id ?? 0} />
                                            {e?.email}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-primary"
                        onClick={() => setShowManagerModal({ ...showManagerModal, show: false })}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <div
                className="card mb-5 p-4"
                onKeyDown={e => {
                    if (e.key === 'Enter') handleClick();
                }}
            >
                <CampaignFilter
                    filterParams={filterParams}
                    setFilterParams={setFilterParams}
                    supervisors={supervisors}
                    handleChange={handleChange}
                    handleClick={handleClick}
                />
            </div>

            <TableLayout
                pagination={state}
                listLength={campaign.length}
                handlePages={handlePages}
                isLoading={dataLoading}
                dataName={'campaigns'}
                skeletonColumns={CAMPAIGN_COLUMNS}
                skeletonRows={state.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {CAMPAIGN_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {campaign?.map(item => (
                            <tr key={item?.id}>
                                <td>{item?.id}</td>
                                <td className="word-break max-w-120px">{item?.title}</td>
                                <td className="word-break">
                                    <ImpersonateEye id={item?.user?.id ?? 0} />
                                    {item?.company?.displayName}
                                </td>
                                <td className="word-break">
                                    {item?.assignedClients?.length > 0 ? (
                                        <>
                                            <ImpersonateEye id={item.assignedClients[0]?.id ?? 0} />
                                            {item.assignedClients[0]?.brandCompany?.displayName}
                                        </>
                                    ) : (
                                        <span className="word-break text-danger">
                                            <FaTimes />
                                        </span>
                                    )}
                                </td>
                                <td className="text-capitalize">{item?.phase ?? '-'}</td>
                                <td>{item?.feePercentage ? calculateFee(item?.feePercentage) : ''}</td>
                                <td>{getCorrectEnums(item?.company?.package?.displayName ?? '')}</td>
                                <td>
                                    {formatDate(item?.publishFrom)}-{formatDate(item?.publishTo)}
                                </td>
                                <td>
                                    {item?.isCompleted ? (
                                        <span className={`status completed`}>completed</span>
                                    ) : (
                                        <span className={`status ${item?.status}`}>
                                            {item?.status === 'published' ? 'public' : item.status}
                                        </span>
                                    )}
                                </td>
                                <td>
                                    <span
                                        className={`status ${
                                            item.isLongTerm ? 'long' : item.parent ? 'linked' : 'standard'
                                        }`}
                                    >
                                        {item.isLongTerm ? 'Long' : item.parent ? 'Linked' : 'Standard'}
                                    </span>
                                </td>
                                <td>
                                    {!item.isDeleted && (
                                        <Button
                                            variant="outline-primary"
                                            className="min-w-max-cont "
                                            onClick={() =>
                                                setShowModal({
                                                    ...showModal,
                                                    showSupervisor: true,
                                                    id: item?.id,
                                                    title: item?.title,
                                                    assignedSupervisors: item?.assignedSupervisors,
                                                })
                                            }
                                        >
                                            Edit ({item?.assignedSupervisors.length})
                                        </Button>
                                    )}
                                </td>
                                <td>{priceSplitter(item?.totalSpent ?? 0)}€</td>
                                <td>
                                    {!item.isDeleted ? (
                                        <DropdownButton title="Actions">
                                            <Dropdown.Item
                                                eventKey="1"
                                                onClick={() =>
                                                    navigate(`/campaign/update/${item?.id}`, { state: { ...item } })
                                                }
                                            >
                                                Edit
                                            </Dropdown.Item>
                                            {hasAccessRight(AdminAclEnum.CAMPAIGN_ADVANCED_EDIT) && (
                                                <Dropdown.Item
                                                    eventKey="1a"
                                                    onClick={() => navigate(`/campaign/advanced-edit/${item?.id}`)}
                                                >
                                                    Advanced edit
                                                </Dropdown.Item>
                                            )}
                                            <Dropdown.Item
                                                onClick={() =>
                                                    navigate(`/campaign/transfer/${item?.id}`, {
                                                        state: {
                                                            companyName: item?.company?.displayName,
                                                            campaign: item,
                                                        },
                                                    })
                                                }
                                                eventKey="2"
                                            >
                                                Transfer
                                            </Dropdown.Item>
                                            {item?.assignedClients?.length > 0 ? (
                                                <Dropdown.Item
                                                    onClick={() =>
                                                        setShowModal({
                                                            ...showModal,
                                                            showClient: true,
                                                            id: item?.id,
                                                            title: item?.title,
                                                            assignedClients: item?.assignedClients,
                                                        })
                                                    }
                                                >
                                                    Clients ({item?.assignedClients.length})
                                                </Dropdown.Item>
                                            ) : null}
                                            {!item.isDeleted ? (
                                                <Dropdown.Item
                                                    onClick={() =>
                                                        setShowManagerModal({
                                                            ...showManagerModal,
                                                            managers: item?.assignedUsers,
                                                            show: true,
                                                        })
                                                    }
                                                >
                                                    Managers({item?.assignedUsers?.length})
                                                </Dropdown.Item>
                                            ) : null}
                                            {item.hasExpiredOffers && (
                                                <Dropdown.Item
                                                    eventKey="1"
                                                    onClick={() => {
                                                        dispatch(
                                                            generalSlice.actions.setConfirmModal({
                                                                show: true,
                                                                title: 'Campaign communication',
                                                                contentBody: (
                                                                    <div>
                                                                        Are you sure you want to rollback expired
                                                                        offers?
                                                                    </div>
                                                                ),
                                                                action: () => rollBackExpired(item.id),
                                                            })
                                                        );
                                                    }}
                                                >
                                                    Rollback Expired Offers
                                                </Dropdown.Item>
                                            )}
                                            <Dropdown.Divider />
                                            {!item?.isCompleted ? (
                                                <Dropdown.Item
                                                    onClick={() =>
                                                        setShowModal({
                                                            ...showModal,
                                                            show: true,
                                                            id: item?.id,
                                                            title: item?.title,
                                                        })
                                                    }
                                                    eventKey="3"
                                                >
                                                    Complete
                                                </Dropdown.Item>
                                            ) : (
                                                <Dropdown.Item eventKey="4" className="bg-primary-o-50 text-primary">
                                                    Completed
                                                </Dropdown.Item>
                                            )}
                                        </DropdownButton>
                                    ) : (
                                        <h6 className="text-danger">Deleted</h6>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {campaign.length < 15 && !!campaign.length && <div className="h-180px"></div>}
                    </tbody>
                </Table>
            </TableLayout>
        </>
    );
}
